import { useEffect } from 'react';
import usePlanStore, { YouTubeConfig } from '../../../../../../store/planStore';
import { YouTubeTouchpointSetup, YTTargetGroup } from '../interfaces';

const useYoutubeSliders = (fieldConfig?: YouTubeTouchpointSetup) => {
  const { countryForms, selectedCountry, updateYouTubeConfig } = usePlanStore();

  const maxSliderValue = 1;

  const youTubeConfig: YouTubeConfig | undefined =
    countryForms[selectedCountry!]?.youTubeConfig;

  const handleSaturationChange = (value: number) => {
    updateYouTubeConfig(selectedCountry!, { saturation: value });
  };

  const handleTargetGroupChange = (value: YTTargetGroup) => {
    updateYouTubeConfig(selectedCountry!, { targetGroup: value });
  };

  const handleBumperChange = (newBumper: number) => {
    const isSlidersOverMax =
      newBumper + (youTubeConfig?.unSkipValue ?? 0) > maxSliderValue;
    const isOppositeLowest =
      youTubeConfig?.unSkipValue === fieldConfig?.unskipMin;
    const maximumPossibleValue =
      maxSliderValue - (youTubeConfig?.unSkipValue ?? 0);
    let validatedBumper: number | undefined = newBumper;

    if (isSlidersOverMax && isOppositeLowest) {
      if (newBumper > (youTubeConfig?.bumperValue ?? 0)) {
        return;
      }
      validatedBumper = maximumPossibleValue;
    }

    const unSkip = youTubeConfig?.unSkipValue ?? 0;
    let adjustedUnSkip = unSkip;

    if (newBumper + unSkip > maxSliderValue) {
      adjustedUnSkip = Math.max(maxSliderValue - newBumper, 0);
    }

    const newSkip = Math.max(maxSliderValue - newBumper - adjustedUnSkip, 0);

    updateYouTubeConfig(selectedCountry!, {
      bumperValue: validatedBumper,
      unSkipValue: adjustedUnSkip,
      skipValue: newSkip,
    });
  };

  const handleUnSkipChange = (unSkip: number) => {
    const isSlidersOverMax =
      unSkip + (youTubeConfig?.bumperValue ?? 0) > maxSliderValue;
    const isOppositeLowest =
      youTubeConfig?.bumperValue === fieldConfig?.bumperMin;
    const maximumPossibleValue =
      maxSliderValue - (youTubeConfig?.bumperValue ?? 0);
    let validatedUnSkip: number | undefined = unSkip;

    if (isSlidersOverMax && isOppositeLowest) {
      if (unSkip > (youTubeConfig?.unSkipValue ?? 0)) {
        return;
      }
      validatedUnSkip = maximumPossibleValue;
    }
    const newSkip = Math.max(
      maxSliderValue - unSkip - (youTubeConfig?.bumperValue ?? 0),
      0
    );

    updateYouTubeConfig(selectedCountry!, {
      unSkipValue: validatedUnSkip,
      skipValue: newSkip,
    });
  };

  useEffect(() => {
    if (
      (youTubeConfig?.unSkipValue ?? 0) + (youTubeConfig?.bumperValue ?? 0) <=
      maxSliderValue
    ) {
      return;
    }
    if (fieldConfig?.unskipMax) {
      if (youTubeConfig?.bumperValue !== undefined) {
        const adjustedUnSkipValue =
          maxSliderValue - (youTubeConfig.bumperValue ?? 0);

        if (adjustedUnSkipValue < (fieldConfig.unskipMin ?? 0)) {
          updateYouTubeConfig(selectedCountry!, {
            unSkipValue: fieldConfig.unskipMin,
          });
        } else {
          updateYouTubeConfig(selectedCountry!, {
            unSkipValue: Math.max(+adjustedUnSkipValue.toPrecision(2), 0),
          });
        }
      }
    }
  }, [youTubeConfig?.bumperValue]);

  useEffect(() => {
    if (
      (youTubeConfig?.unSkipValue ?? 0) + (youTubeConfig?.bumperValue ?? 0) <=
      maxSliderValue
    ) {
      return;
    }
    if (youTubeConfig?.bumperValue) {
      if (youTubeConfig?.unSkipValue !== undefined) {
        const adjustedBumperValue =
          maxSliderValue - (youTubeConfig.unSkipValue ?? 0);
        if (adjustedBumperValue < (fieldConfig?.bumperMin ?? 0)) {
          updateYouTubeConfig(selectedCountry!, {
            bumperValue: fieldConfig?.bumperMin,
          });
        } else {
          updateYouTubeConfig(selectedCountry!, {
            bumperValue: Math.max(+adjustedBumperValue.toPrecision(2), 0),
          });
        }
      }
    }
  }, [youTubeConfig?.unSkipValue]);

  return {
    handleBumperChange,
    handleSaturationChange,
    handleUnSkipChange,
    handleTargetGroupChange,
  };
};

export default useYoutubeSliders;
